import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchChats as fetchChatsService,
  loadChat as loadChatService,
  updateChat,
  deleteChat as deleteChatService,
} from "../services/chatService";
import { restoreFile, getChatFiles } from "../services/fileService";
import { removeCitationMarkers } from "../utils/textProcessing";
import { convertTextToHtml } from "../utils/textProcessing";
import { useNotification } from "../context/NotificationContext";

// Cache last chat list per user so returning to dashboard shows list instantly while refetching
const CHAT_LIST_CACHE_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
let chatListCache = { userId: null, chats: [], page: 1, hasMore: false, ts: 0 };

/** Axios/mobile: request never reached server (suspended tab, flaky radio, etc.). */
function isTransientNetworkError(error) {
  if (!error || error.response) return false;
  const code = String(error.code || "").toUpperCase();
  const msg = String(error.message || "").toLowerCase();
  return (
    code === "ERR_NETWORK" ||
    code === "ECONNRESET" ||
    msg.includes("network error") ||
    msg.includes("load failed") ||
    msg.includes("failed to fetch") ||
    msg.includes("timeout") ||
    msg.includes("socket hang up")
  );
}

const DEFAULT_COMPARISON_QUERY =
  "Analyze the AI responses and provide a tactical comparative analysis for litigation planning. Identify where the models agree and disagree on legal strategy, risk assessment, and recommended actions. Rank critical vulnerabilities by litigation impact and recommend which position is strongest based on legal principles and factual support. Be direct and actionable—focus on what needs to be strengthened, corrected, or mitigated. Identify which AI provided the most useful analysis for this specific legal issue.";
const DEFAULT_SYNTHESIS_QUERY =
  "Produce a final legal synthesis for litigation planning using ONLY the AI responses from the Initial Query and, if available, the Comparative Analysis. Do not introduce new facts, cases, statutes, or legal theories not mentioned by the models. Analyze the issues, apply the relevant legal principles, and provide a clear, attorney-ready assessment of the client's position. Identify the primary and secondary vulnerabilities, assess whether the position is STRONG, MODERATE, or WEAK, and provide specific corrective actions to strengthen the case. Be direct and practical—focus on what will materially impact litigation outcome. Structure the response using IRAC+ format with clear section headings and lawyer-friendly language.";

export function useChats({
  user,
  setQuery,
  setLastSubmittedQuery,
  setLastSubmittedFiles,
  setResponses,
  setSelectedModels,
  setComparisonQuery,
  setComparisonResult,
  setComparisonGenerated,
  setComparisonHistory,
  setSelectedComparisonHistoryIndex,
  setSynthesisQuery,
  setSynthesisResult,
  setSynthesisGenerated,
  setSynthesisHistory,
  setSelectedSynthesisHistoryIndex,
  setComparisonModel,
  setSynthesisModel,
  setChatFiles,
  getCurrentChatFiles,
  setCurrentChatFile,
  setSynthesisModelsAnalyzed,
}) {
  const navigate = useNavigate();
  const { showError, showSuccess, showWarning, confirm } = useNotification();

  const [chats, setChats] = useState([]);
  // Starts true only when there is a user whose chats are about to load.
  // Dashboard gates every startup fetch on `user`, so for a guest fetchChats
  // is never called and its own "clear for guests" branch never ran — the
  // flag stayed true for the whole visit and, because the Comparative
  // Analysis model buttons are disabled while chats load, a guest could not
  // switch the comparison model to Claude (the click did nothing, with no
  // visual cue). The effect below covers a user who signs out mid-session.
  const [loadingChats, setLoadingChats] = useState(() => !!user);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chatsPage, setChatsPage] = useState(1);
  const [chatsHasMore, setChatsHasMore] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [deletingChatId, setDeletingChatId] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    if (!user) setLoadingChats(false);
  }, [user]);

  const fetchChats = useCallback(
    async (signal = null, { page = 1, limit = 8 } = {}) => {
      if (!user) {
        // Clear the initial loading state so guest users are NOT stuck with
        // disabled model buttons (loadingChats starts as `true`).
        setLoadingChats(false);
        return;
      }

      const useCache =
        page === 1 &&
        chatListCache.userId === user.userId &&
        chatListCache.chats.length > 0 &&
        Date.now() - chatListCache.ts < CHAT_LIST_CACHE_MAX_AGE_MS;
      if (useCache) {
        setChats(chatListCache.chats);
        setChatsPage(chatListCache.page);
        setChatsHasMore(chatListCache.hasMore);
        setLoadingChats(false);
      } else if (page === 1) {
        setLoadingChats(true);
      }

      try {
        const {
          chats: chatsWithIds,
          page: currentPage,
          hasMore,
        } = await fetchChatsService(signal, { page, limit });

        if (signal?.aborted) return;
        setChats(chatsWithIds);
        setChatsPage(currentPage);
        setChatsHasMore(hasMore);
        if (page === 1 && user?.userId) {
          chatListCache = {
            userId: user.userId,
            chats: chatsWithIds,
            page: currentPage,
            hasMore,
            ts: Date.now(),
          };
        }
      } catch (error) {
        if (
          error.response?.status === 401 ||
          error.name === "AbortError" ||
          error.name === "CanceledError"
        ) {
          return;
        }
        console.error("Error fetching chats:", error);
      } finally {
        if (!useCache) setLoadingChats(false);
      }
    },
    [user],
  );

  const loadMoreChats = useCallback(async () => {
    if (!user || loadingMore || !chatsHasMore) return;
    try {
      setLoadingMore(true);
      const nextPage = chatsPage + 1;
      const {
        chats: nextChats,
        page: currentPage,
        hasMore,
      } = await fetchChatsService(null, { page: nextPage, limit: 8 });
      setChats((prev) => [...prev, ...nextChats]);
      setChatsPage(currentPage);
      setChatsHasMore(hasMore);
    } catch (error) {
      if (
        error.response?.status === 401 ||
        error.name === "AbortError" ||
        error.name === "CanceledError"
      ) {
        return;
      }
      console.error("Error loading more chats:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [user, loadingMore, chatsHasMore, chatsPage]);

  const loadChat = useCallback(
    async (chatId, currentChatIdValue, responses, query) => {
      if (!user) return;

      if (!chatId || typeof chatId !== "string" || chatId.trim() === "") {
        console.error("Invalid chatId:", chatId);
        showError("Invalid chat ID");
        return;
      }

      // Don't reload only when we're already viewing THIS chat AND we have full data for it.
      // After refresh, responses/query are empty so we must always load from backend.
      const chatIdToCompare = String(chatId).trim();
      const currentChatIdStr =
        currentChatIdValue != null ? String(currentChatIdValue).trim() : "";
      const isSameChat =
        currentChatIdStr === chatIdToCompare ||
        (currentChatIdStr &&
          chatIdToCompare &&
          currentChatIdStr === chatIdToCompare);
      const hasResponses = Object.keys(responses || {}).length > 0;
      const hasQuery = query != null && String(query).trim().length > 0;
      const hasFullDataForThisChat = isSameChat && hasResponses && hasQuery;

      if (hasFullDataForThisChat) {
        console.log(
          "Skipping loadChat - already viewing this chat with responses and query",
        );
        return;
      }

      setLoadingChat(true);
      try {
        console.log(
          "[CHAT LOAD/REFRESH] ============================================",
        );
        console.log("[CHAT LOAD/REFRESH] Timestamp:", new Date().toISOString());
        console.log("[CHAT LOAD/REFRESH] Requested chatId:", chatId);
        console.log(
          "[CHAT LOAD/REFRESH] currentChatIdValue:",
          currentChatIdValue,
        );
        console.log("[CHAT LOAD/REFRESH] isSameChat check:", isSameChat);
        console.log("[CHAT LOAD/REFRESH] Loading chat from backend...");

        let chat;
        let loadAttempt = 0;
        const maxLoadAttempts = 4;
        while (loadAttempt < maxLoadAttempts) {
          try {
            chat = await loadChatService(chatId);
            break;
          } catch (e) {
            loadAttempt += 1;
            if (
              loadAttempt >= maxLoadAttempts ||
              !isTransientNetworkError(e)
            ) {
              throw e;
            }
            await new Promise((r) => setTimeout(r, 350 * loadAttempt));
          }
        }

        // LOG: Chat loaded/refreshed with all IDs
        console.log("[CHAT LOAD/REFRESH] Chat loaded successfully");
        console.log("[CHAT LOAD/REFRESH] MongoDB _id:", chat._id);
        console.log("[CHAT LOAD/REFRESH] 16-digit chatId:", chat.chatId);
        console.log(
          "[CHAT LOAD/REFRESH] chatId16 (alias):",
          chat.chatId16 || chat.chatId,
        );
        console.log("[CHAT LOAD/REFRESH] userId:", chat.userId);
        console.log("[CHAT LOAD/REFRESH] title:", chat.title);
        console.log("[CHAT LOAD/REFRESH] query:", chat.query);
        console.log("[CHAT LOAD/REFRESH] selectedModels:", chat.selectedModels);
        console.log(
          "[CHAT LOAD/REFRESH] hasResponses:",
          !!chat.responses && Object.keys(chat.responses).length > 0,
        );
        console.log(
          "[CHAT LOAD/REFRESH] ============================================",
        );

        // Process responses: support both string and object { text } / { content } from API/DB.
        // After page refresh, backend may return plain object; ensure we always extract text.
        const formattedResponses = {};
        if (chat.responses && typeof chat.responses === "object") {
          const rawResponses =
            chat.responses instanceof Map
              ? Object.fromEntries(chat.responses)
              : chat.responses;
          Object.keys(rawResponses).forEach((model) => {
            const raw = rawResponses[model];
            const responseText =
              typeof raw === "string"
                ? raw
                : raw && (raw.text ?? raw.content)
                  ? String(raw.text ?? raw.content)
                  : "";
            // If the backend stored a formatted HTML (from a previous edit),
            // use it directly instead of re-converting from text, which would
            // produce different formatting and change the response template.
            const savedFormatted =
              typeof raw === "object" && raw !== null && raw.formatted
                ? raw.formatted
                : null;
            if (responseText && responseText.trim()) {
              const cleaned = removeCitationMarkers(responseText);
              formattedResponses[model] = {
                text: cleaned,
                formatted: savedFormatted || convertTextToHtml(cleaned),
                loading: false,
                error: null,
              };
            }
          });
        }

        // Use the chat's selectedModels from database, don't filter by responses
        // This ensures we show the models that were selected, even if responses aren't loaded yet
        const chatSelectedModels =
          chat.selectedModels &&
          Array.isArray(chat.selectedModels) &&
          chat.selectedModels.length > 0
            ? chat.selectedModels
            : ["ChatGPT", "Gemini"]; // Fallback to default only if no selectedModels in chat

        // Determine effective chat ID
        const effectiveChatId = chat.chatId || chat._id;

        // Navigate if needed
        const chatId16 = chat.chatId16 || chat.chatId || chat._id;
        if (chatId16 && chatId16 !== chatId) {
          navigate(`/research/${chatId16}`, { replace: true });
        }

        // Set state
        setCurrentChatId(effectiveChatId);
        const chatQuery =
          chat.query !== null && chat.query !== undefined
            ? String(chat.query)
            : "";
        setQuery(chatQuery);
        setLastSubmittedQuery(chatQuery);
        // Note: lastSubmittedFiles will be set after files are restored (below)
        // Use the chat's selectedModels, not filtered by responses
        setSelectedModels(chatSelectedModels);
        setResponses(formattedResponses);

        // Set comparison and synthesis data
        setComparisonQuery(chat.comparisonQuery || DEFAULT_COMPARISON_QUERY);
        setComparisonResult(removeCitationMarkers(chat.comparisonResult || ""));
        setComparisonGenerated(!!chat.comparisonResult);
        setSynthesisQuery(chat.synthesisQuery || DEFAULT_SYNTHESIS_QUERY);
        setSynthesisResult(removeCitationMarkers(chat.synthesisResult || ""));
        setSynthesisGenerated(!!chat.synthesisResult);
        setComparisonModel(chat.comparisonModel || "Gemini");
        setSynthesisModel(chat.synthesisModel || "ChatGPT");

        // Set history
        if (
          chat.comparisonHistory &&
          Array.isArray(chat.comparisonHistory) &&
          chat.comparisonHistory.length > 0
        ) {
          const cleanedComparisonHistory = chat.comparisonHistory.map(
            (entry) => ({
              ...entry,
              result: removeCitationMarkers(entry.result || ""),
            }),
          );
          setComparisonHistory(cleanedComparisonHistory);
        } else if (chat.comparisonResult && chat.comparisonModel) {
          setComparisonHistory([
            {
              model: chat.comparisonModel,
              query: chat.comparisonQuery || "",
              result: removeCitationMarkers(chat.comparisonResult || ""),
              timestamp: chat.updatedAtISO || new Date().toISOString(),
            },
          ]);
        } else {
          setComparisonHistory([]);
        }

        if (
          chat.synthesisHistory &&
          Array.isArray(chat.synthesisHistory) &&
          chat.synthesisHistory.length > 0
        ) {
          const cleanedSynthesisHistory = chat.synthesisHistory.map(
            (entry) => ({
              ...entry,
              result: removeCitationMarkers(entry.result || ""),
            }),
          );
          setSynthesisHistory(cleanedSynthesisHistory);
          if (
            setSynthesisModelsAnalyzed &&
            cleanedSynthesisHistory[0]?.modelsAnalyzed
          ) {
            setSynthesisModelsAnalyzed(
              cleanedSynthesisHistory[0].modelsAnalyzed,
            );
          }
        } else if (chat.synthesisResult && chat.synthesisModel) {
          setSynthesisHistory([
            {
              model: chat.synthesisModel,
              query: chat.synthesisQuery || "",
              result: removeCitationMarkers(chat.synthesisResult || ""),
              timestamp: chat.updatedAtISO || new Date().toISOString(),
            },
          ]);
          if (setSynthesisModelsAnalyzed) setSynthesisModelsAnalyzed([]);
        } else {
          setSynthesisHistory([]);
          if (setSynthesisModelsAnalyzed) setSynthesisModelsAnalyzed([]);
        }

        // Handle attachedFiles using the new getChatFiles API
        const effectiveChatIdStr = String(effectiveChatId);
        let restoredFiles = {};

        try {
          console.log(
            `[useChats] Fetching chat files for chatId ${effectiveChatIdStr}...`,
          );
          const chatFilesFromApi = await getChatFiles(effectiveChatIdStr);

          // Expected shape from API: Array of { fileId, chatId, fileName, fileSize, fileType, s3Key, s3Url, uploadedAt, committedAt }
          if (chatFilesFromApi && chatFilesFromApi.length > 0) {
            // How does the backend map file categories? Use chat.attachedFiles as a map or infer from shape.
            // As per instructions, chat.attachedFiles preserves categories keyed by lists, but now containing fileId objects.
            const attachedFilesMap = chat.attachedFiles || {};

            chatFilesFromApi.forEach((apiFile) => {
              // Find which category this file belongs to via chat.attachedFiles map
              let category = null;
              for (const [key, filesList] of Object.entries(attachedFilesMap)) {
                if (
                  Array.isArray(filesList) &&
                  filesList.some((f) => f.fileId === apiFile.fileId)
                ) {
                  category = key;
                  break;
                }
              }

              if (category) {
                if (!restoredFiles[category]) restoredFiles[category] = [];
                restoredFiles[category].push({
                  fileId: apiFile.fileId,
                  fileName: apiFile.fileName,
                  fileSize: apiFile.fileSize,
                  fileType: apiFile.fileType,
                  s3Key: apiFile.s3Key,
                  s3Url: apiFile.s3Url, // Fresh signed URL
                  fileUrl: apiFile.s3Url, // For fallback display compatibility
                });
              }
            });
          }
        } catch (filesErr) {
          console.error(
            "[useChats] Error fetching chat files via GET /files",
            filesErr,
          );
        }

        if (Object.keys(restoredFiles).length > 0) {
          setChatFiles((prev) => ({
            ...prev,
            [effectiveChatId]: restoredFiles,
          }));

          // Set lastSubmittedFiles after files are restored
          if (setLastSubmittedFiles) {
            const lastFiles = {};

            // New array format
            if (
              restoredFiles.mainQueryFiles &&
              Array.isArray(restoredFiles.mainQueryFiles)
            ) {
              lastFiles.mainQueryFiles = restoredFiles.mainQueryFiles.map(
                (f) => ({
                  fileId: f.fileId,
                  s3Key: f.s3Key,
                  fileName: f.fileName,
                  fileType: f.fileType,
                }),
              );
            }

            if (Object.keys(lastFiles).length > 0) {
              setLastSubmittedFiles(lastFiles);
            } else {
              setLastSubmittedFiles(null);
            }
          }
        } else if (setLastSubmittedFiles) {
          // No files restored, set to null
          setLastSubmittedFiles(null);
        }
      } catch (error) {
        console.error("Error loading chat:", error);

        // If chat is not found (404), don't show error - just throw so caller can handle it
        // This is expected if chat was deleted
        if (
          error.response?.status === 404 ||
          error.message === "Chat not found"
        ) {
          console.warn("Chat not found, will be handled by caller:", chatId);
          throw error;
        }

        // For other errors, show message (softer copy when OS just resumed the browser tab)
        if (isTransientNetworkError(error)) {
          showWarning(
            "LawFusion couldn't reach the server right after returning from the background. Wait a moment and open this chat again, or refresh the page.",
            "Connection paused",
          );
        } else {
          showError(
            "Failed to load chat: " +
              (error.response?.data?.message || error.message),
          );
        }
        throw error;
      } finally {
        setLoadingChat(false);
      }
    },
    [
      user,
      navigate,
      setQuery,
      setLastSubmittedQuery,
      setResponses,
      setSelectedModels,
      setComparisonQuery,
      setComparisonResult,
      setComparisonGenerated,
      setComparisonHistory,
      setSynthesisQuery,
      setSynthesisResult,
      setSynthesisGenerated,
      setSynthesisHistory,
      setComparisonModel,
      setSynthesisModel,
      setChatFiles,
      showError,
      showWarning,
    ],
  );

  const createNewChat = useCallback(() => {
    setQuery("");
    setLastSubmittedQuery("");
    if (setLastSubmittedFiles) {
      setLastSubmittedFiles(null);
    }
    setResponses({});
    setComparisonQuery(DEFAULT_COMPARISON_QUERY);
    setComparisonResult("");
    setComparisonGenerated(false);
    setComparisonHistory([]);
    setSelectedComparisonHistoryIndex(null);
    setSynthesisQuery(DEFAULT_SYNTHESIS_QUERY);
    setSynthesisResult("");
    setSynthesisGenerated(false);
    setSynthesisHistory([]);
    setSelectedSynthesisHistoryIndex(null);
    setSelectedModels(["ChatGPT", "Gemini"]);
    setComparisonModel("Gemini");
    setSynthesisModel("ChatGPT");

    setChatFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles["new"];
      return newFiles;
    });

    setCurrentChatId(null);
    navigate("/research", { replace: true });
  }, [
    navigate,
    setQuery,
    setLastSubmittedQuery,
    setLastSubmittedFiles,
    setResponses,
    setComparisonQuery,
    setComparisonResult,
    setComparisonGenerated,
    setComparisonHistory,
    setSelectedComparisonHistoryIndex,
    setSynthesisQuery,
    setSynthesisResult,
    setSynthesisGenerated,
    setSynthesisHistory,
    setSelectedSynthesisHistoryIndex,
    setSelectedModels,
    setComparisonModel,
    setSynthesisModel,
    setChatFiles,
  ]);

  /** Save chat title (used by Rename dialog). */
  const saveChatTitle = useCallback(
    async (chatId, newTitle) => {
      const trimmed = (newTitle || "").trim();
      if (!trimmed) {
        showWarning("Title cannot be empty");
        return;
      }
      const chat = chats.find((c) => c._id === chatId || c.chatId === chatId);
      if (!chat) {
        showError("Chat not found");
        return;
      }
      const chatIdToUpdate = chat.chatId || chat._id;
      try {
        await updateChat(chatIdToUpdate, { title: trimmed });
        setChats(
          chats.map((c) =>
            c._id === chatId || c.chatId === chatId
              ? { ...c, title: trimmed }
              : c,
          ),
        );
        showSuccess("Chat title updated successfully");
      } catch (error) {
        console.error("Error updating chat title:", error);
        showError("Failed to update chat title. Please try again.");
      }
    },
    [chats, showWarning, showSuccess, showError],
  );

  /** Move chat to a project (or remove from project if projectId is null). */
  const addChatToProject = useCallback(
    async (chatId, projectId) => {
      const chat = chats.find((c) => c._id === chatId || c.chatId === chatId);
      if (!chat) {
        showError("Chat not found");
        return;
      }
      const chatIdToUpdate = chat.chatId || chat._id;
      try {
        await updateChat(chatIdToUpdate, { projectId: projectId || null });
        setChats(
          chats.map((c) =>
            c._id === chatId || c.chatId === chatId
              ? { ...c, projectId: projectId || null }
              : c,
          ),
        );
        showSuccess(
          projectId ? "Chat added to project" : "Chat removed from project",
        );
      } catch (error) {
        console.error("Error updating chat project:", error);
        showError("Failed to update project. Please try again.");
      }
    },
    [chats, showSuccess, showError],
  );

  const deleteChat = useCallback(
    async (chatId, e) => {
      if (e) {
        e.stopPropagation();
      }

      const confirmed = await confirm({
        title: "Delete Chat",
        message:
          "Are you sure you want to delete this chat? This action cannot be undone.",
        confirmText: "Delete",
        cancelText: "Cancel",
        type: "danger",
      });

      if (!confirmed) {
        return;
      }

      try {
        // Find the chat before deleting to log all its IDs
        const chatToDelete = chats.find(
          (c) =>
            c._id === chatId || c.chatId === chatId || c.chatId16 === chatId,
        );
        const deletedChatId16 =
          chatToDelete?.chatId16 || chatToDelete?.chatId || chatId;
        const deletedMongoId = chatToDelete?._id || chatId;

        // LOG: Chat deletion with all IDs
        console.log(
          "[CHAT DELETED] ============================================",
        );
        console.log("[CHAT DELETED] Timestamp:", new Date().toISOString());
        console.log("[CHAT DELETED] MongoDB _id:", deletedMongoId);
        console.log("[CHAT DELETED] 16-digit chatId:", deletedChatId16);
        console.log("[CHAT DELETED] chatId16 (alias):", chatToDelete?.chatId16);
        console.log("[CHAT DELETED] Deleted by chatId param:", chatId);
        console.log("[CHAT DELETED] title:", chatToDelete?.title);
        console.log("[CHAT DELETED] userId:", chatToDelete?.userId);
        console.log(
          "[CHAT DELETED] ============================================",
        );

        setDeletingChatId(chatId);
        try {
          await deleteChatService(chatId);
          setChats(chats.filter((chat) => chat._id !== chatId));

          if (
            currentChatId === chatId ||
            deletedChatId16 === chatId ||
            currentChatId === deletedMongoId
          ) {
            // Create new chat if we're viewing the deleted chat
            createNewChat();
          }
        } finally {
          setDeletingChatId(null);
        }
      } catch (error) {
        console.error("Error deleting chat:", error);
        showError("Failed to delete chat");
        setDeletingChatId(null);
        throw error;
      }
    },
    [chats, currentChatId, confirm, showError, createNewChat],
  );

  return {
    chats,
    setChats,
    loadingChats,
    loadingChat,
    loadingMore,
    chatsHasMore,
    loadMoreChats,
    currentChatId,
    setCurrentChatId,
    fetchChats,
    loadChat,
    createNewChat,
    saveChatTitle,
    addChatToProject,
    deleteChat,
    deletingChatId,
  };
}
